import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type CasoDetalle,
  type Recordatorio,
  type UserMini,
} from "@/lib/api";
import { useAuth, canEdit } from "@/lib/auth";
import { activarWebPush } from "@/lib/push";
import { describeCuando } from "@/lib/fechas";
import AyudaVista from "@/ui/AyudaVista";
import { useToast } from "@/ui/Toast";
import { ayudaRecordatorios } from "@/components/ayuda/vistas/recordatorios";

type Props = {
  caso: CasoDetalle;
  asignables: UserMini[];
};

type Unidad = "m" | "h" | "d";

const UNIDAD_LABEL: Record<Unidad, string> = {
  m: "minutos",
  h: "horas",
  d: "días",
};

// Opciones del menú "Posponer".
//
// Las relativas («en 3 horas») sirven para lo inmediato, pero la mayoría de los
// recordatorios de esta mesa son «esto lo veo mañana a primera hora» — y con
// «1 día» un aviso de las 11 p.m. vuelve a sonar a las 11 p.m. Por eso van
// también las de HORA FIJA y la de elegir fecha y hora a mano (Joe, 2026-08-19).
const HORA_MANANA = 9;
const MIN_MANANA = 30;

const SNOOZE_RELATIVAS: { label: string; cantidad: number; unidad: Unidad }[] = [
  { label: "10 min", cantidad: 10, unidad: "m" },
  { label: "30 min", cantidad: 30, unidad: "m" },
  { label: "1 hora", cantidad: 1, unidad: "h" },
  { label: "3 horas", cantidad: 3, unidad: "h" },
  { label: "1 día", cantidad: 1, unidad: "d" },
];

/** Las 9:30 a.m. de hoy o de mañana, en hora LOCAL. */
function aLasNueveTreinta(diasDespues: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + diasDespues);
  d.setHours(HORA_MANANA, MIN_MANANA, 0, 0);
  return d;
}

/** «Hoy 9:30 a.m.» solo si todavía no pasa: posponer al pasado no existe. */
function hoyTodaviaSirve(): boolean {
  return aLasNueveTreinta(0).getTime() > Date.now() + 60_000;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function estadoBadge(r: Recordatorio): { label: string; cls: string } {
  if (r.estado === "completado")
    return {
      label: "Completado",
      cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    };
  if (r.estado === "cancelado")
    return {
      label: "Cancelado",
      cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
    };
  if (r.disparado_at)
    return {
      label: "Avisado",
      cls: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    };
  return {
    label: "Programado",
    cls: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  };
}

/** Una fecha en el formato que espera `<input type="datetime-local">` (local). */
function paraInputLocal(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/** "ahora + 5 min" en formato local para el atributo min de un
 *  <input type="datetime-local">. */
function minDateTimeLocal(): string {
  const t = new Date(Date.now() + 5 * 60_000);
  const local = new Date(t.getTime() - t.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export default function TabRecordatorios({ caso, asignables }: Props) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  // Caso cerrado = solo lectura: ni recordatorios nuevos ni acciones sobre
  // los que hay. No queda nada que limpiar: al cerrar, el servidor cancela los
  // pendientes (`services/cierre.efectos_de_cierre`, decisión de Joe
  // 2026-10-13). Antes este comentario prometía las acciones y el código las
  // escondía, y los pendientes se quedaban colgados para siempre.
  const puedeEditar = canEdit(me) && caso.estado !== "Cerrado";

  const [items, setItems] = useState<Recordatorio[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Form de captura ---
  const [modo, setModo] = useState<"relativo" | "exacto">("relativo");
  const [cantidad, setCantidad] = useState(10);
  const [unidad, setUnidad] = useState<Unidad>("m");
  const [fechaExacta, setFechaExacta] = useState("");
  // Recordatorio cuyo «elegir fecha y hora» está abierto, y el valor tecleado.
  const [posponiendo, setPosponiendo] = useState<number | null>(null);
  const [posponerFecha, setPosponerFecha] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [destUsers, setDestUsers] = useState<number[]>(() => {
    const ids = [caso.abierto_por_id, caso.asignado_a_id].filter(
      (x): x is number => x != null,
    );
    return [...new Set(ids)];
  });
  const [destEmails, setDestEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Permiso de notificaciones del navegador (popup del SO).
  const [permiso, setPermiso] = useState<NotificationPermission | "unsupported">(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const list = await api.get<Recordatorio[]>(
        `/api/casos/${caso.id}/recordatorios`,
      );
      setItems(list);
    } catch (err) {
      if (!quiet) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar recordatorios", msg);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  // Auto-refresh silencioso cada 30s (igual que Bitacora y Correos): para
  // ver cuando un recordatorio se dispara o lo cambia otro usuario.
  useEffect(() => {
    function tick() {
      if (!document.hidden) void load(true);
    }
    const id = setInterval(tick, 30_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  function toggleUser(id: number) {
    setDestUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function agregarEmail() {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      toast.error("Correo inválido", e);
      return;
    }
    if (!destEmails.includes(e)) setDestEmails((prev) => [...prev, e]);
    setEmailInput("");
  }

  // Validacion del minimo de 5 minutos en el cliente (el server tambien valida).
  const muyCorto =
    modo === "relativo"
      ? unidad === "m" && cantidad < 5
      : !!fechaExacta &&
        new Date(fechaExacta).getTime() < Date.now() + 5 * 60_000;

  const sinDestinatarios = destUsers.length === 0 && destEmails.length === 0;
  const puedeCrear =
    puedeEditar &&
    !submitting &&
    mensaje.trim().length > 0 &&
    !muyCorto &&
    (modo === "relativo" ? cantidad > 0 : !!fechaExacta) &&
    !sinDestinatarios;

  async function crear() {
    if (!puedeCrear) return;
    setSubmitting(true);
    try {
      // Primera vez: el click en "Crear" es el gesto que el navegador
      // exige para pedir el permiso de notificaciones. Si ya se decidio
      // no molesta; si lo bloquean, el recordatorio se crea igual.
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "default"
      ) {
        setPermiso(await Notification.requestPermission());
      }
      // Si hay permiso, asegura la suscripcion Web Push (recordatorios
      // que vencen con la app cerrada). Es idempotente.
      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        void activarWebPush();
      }
      const payload: Record<string, unknown> = {
        mensaje: mensaje.trim(),
        destinatarios_users: destUsers,
        destinatarios_emails: destEmails,
      };
      if (modo === "relativo") {
        payload.offset_cantidad = cantidad;
        payload.offset_unidad = unidad;
      } else {
        // El input datetime-local da hora local; la mandamos en UTC ISO.
        payload.remind_at = new Date(fechaExacta).toISOString();
      }
      await api.post(`/api/casos/${caso.id}/recordatorios`, payload);
      toast.success("Recordatorio creado");
      setMensaje("");
      setFechaExacta("");
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude crear el recordatorio", msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function accion(
    r: Recordatorio,
    acc: "completar" | "cancelar" | "posponer",
    snooze?: { cantidad: number; unidad: Unidad } | { cuando: Date },
  ) {
    try {
      const body: Record<string, unknown> = { accion: acc };
      if (acc === "posponer" && snooze) {
        if ("cuando" in snooze) {
          // El endpoint ya aceptaba fecha absoluta (`remind_at`); no hizo falta
          // tocar el backend para las horas fijas ni para el selector.
          body.remind_at = snooze.cuando.toISOString();
        } else {
          body.offset_cantidad = snooze.cantidad;
          body.offset_unidad = snooze.unidad;
        }
      }
      await api.patch(`/api/casos/${caso.id}/recordatorios/${r.id}`, body);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude actualizar el recordatorio", msg);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <AyudaVista titulo="Recordatorios" {...ayudaRecordatorios({ puedeEditar })} />
      </div>
      {/* --- Form de captura --- */}
      {puedeEditar && (
        <div className="card space-y-3">
          <p className="text-sm font-medium">Nuevo recordatorio</p>
          {permiso === "denied" && (
            <p className="text-xs text-zinc-500">
              Los avisos del navegador están bloqueados — el recordatorio
              igual te llega por correo y en Alertas.
            </p>
          )}

          {/* Cuando */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={modo === "relativo"}
                onChange={() => setModo("relativo")}
              />
              Recuérdame en
            </label>
            <input
              type="number"
              min={unidad === "m" ? 5 : 1}
              value={cantidad}
              disabled={modo !== "relativo"}
              onChange={(e) => setCantidad(Number(e.target.value))}
              onBlur={() => {
                const minimo = unidad === "m" ? 5 : 1;
                if (cantidad < minimo) setCantidad(minimo);
              }}
              className="input w-20 disabled:opacity-40"
            />
            <select
              value={unidad}
              disabled={modo !== "relativo"}
              onChange={(e) => {
                const u = e.target.value as Unidad;
                setUnidad(u);
                if (u === "m" && cantidad < 5) setCantidad(5);
              }}
              className="input w-32 disabled:opacity-40"
            >
              {(["m", "h", "d"] as Unidad[]).map((u) => (
                <option key={u} value={u}>
                  {UNIDAD_LABEL[u]}
                </option>
              ))}
            </select>

            <span className="text-zinc-600">o</span>

            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                checked={modo === "exacto"}
                onChange={() => setModo("exacto")}
              />
              El
            </label>
            <input
              type="datetime-local"
              min={minDateTimeLocal()}
              value={fechaExacta}
              disabled={modo !== "exacto"}
              onChange={(e) => setFechaExacta(e.target.value)}
              className="input w-56 disabled:opacity-40"
            />
          </div>
          {muyCorto && (
            <p className="text-xs text-amber-400">
              El recordatorio debe ser para al menos 5 minutos después.
            </p>
          )}

          {/* Mensaje */}
          <div>
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={2}
              placeholder="Qué recordar... (este texto es el que llega en el correo)"
              className="input w-full"
            />
          </div>

          {/* Destinatarios */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-zinc-500">
              Avisar a
            </p>
            <div className="flex flex-wrap gap-1.5">
              {asignables.map((u) => {
                const sel = destUsers.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleUser(u.id)}
                    className={`text-xs px-2 py-1 rounded-full border transition ${
                      sel
                        ? "border-accent-500/50 bg-accent-500/15 text-accent-300"
                        : "border-ink-600 text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {sel ? "✓ " : ""}
                    {u.nombre}
                  </button>
                );
              })}
            </div>
            {destEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {destEmails.map((e) => (
                  <span
                    key={e}
                    className="text-xs px-2 py-1 rounded-full border border-ink-600 bg-ink-800 text-zinc-300 flex items-center gap-1.5"
                  >
                    {e}
                    <button
                      type="button"
                      onClick={() =>
                        setDestEmails((prev) => prev.filter((x) => x !== e))
                      }
                      className="text-zinc-500 hover:text-red-400"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    agregarEmail();
                  }
                }}
                placeholder="Agregar un correo libre (cliente, tercero...)"
                className="input w-full sm:flex-1"
              />
              <button
                type="button"
                onClick={agregarEmail}
                className="btn-ghost text-sm border border-ink-600 w-full sm:w-auto whitespace-nowrap"
              >
                + Agregar correo
              </button>
            </div>
          </div>

          {sinDestinatarios && (
            <p className="text-xs text-amber-400">
              Elige al menos un usuario o agrega un correo.
            </p>
          )}

          <div>
            <button
              onClick={crear}
              disabled={!puedeCrear}
              className="btn-primary disabled:opacity-40"
            >
              Crear recordatorio
            </button>
          </div>
        </div>
      )}

      {/* --- Log de recordatorios --- */}
      <div className="card">
        {loading && items.length === 0 ? (
          <p className="text-sm text-zinc-500">Cargando recordatorios...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Sin recordatorios. Crea el primero arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((r) => {
              const badge = estadoBadge(r);
              const activo = r.estado === "pendiente";
              return (
                <li key={r.id} className="py-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                      <span className="text-sm text-zinc-300">
                        {new Date(r.remind_at).toLocaleString()}
                      </span>
                      <span className="text-xs text-zinc-500">
                        ({describeCuando(r.remind_at)})
                      </span>
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {r.mensaje}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Avisa a:{" "}
                      {[
                        ...r.destinatarios_users_nombres,
                        ...r.destinatarios_emails,
                      ].join(", ") || "—"}
                      {r.creado_por_nombre && ` · creado por ${r.creado_por_nombre}`}
                      {r.email_enviado_at && " · correo enviado"}
                    </p>
                  </div>

                  {puedeEditar && activo && (
                    <div className="flex items-center gap-2 flex-wrap sm:flex-shrink-0">
                      <button
                        onClick={() => void accion(r, "completar")}
                        className="text-xs text-emerald-400 hover:text-emerald-300"
                      >
                        ✓ Hecho
                      </button>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const v = e.target.value;
                          e.target.value = "";
                          if (v === "elegir") {
                            setPosponerFecha(paraInputLocal(aLasNueveTreinta(1)));
                            setPosponiendo(r.id);
                            return;
                          }
                          if (v === "hoy930") {
                            void accion(r, "posponer", { cuando: aLasNueveTreinta(0) });
                            return;
                          }
                          if (v === "manana930") {
                            void accion(r, "posponer", { cuando: aLasNueveTreinta(1) });
                            return;
                          }
                          const op = SNOOZE_RELATIVAS[Number(v)];
                          if (op)
                            void accion(r, "posponer", {
                              cantidad: op.cantidad,
                              unidad: op.unidad,
                            });
                        }}
                        className="input text-xs py-1 w-full sm:w-36"
                      >
                        <option value="" disabled>
                          Posponer...
                        </option>
                        {SNOOZE_RELATIVAS.map((op, i) => (
                          <option key={op.label} value={i}>
                            {op.label}
                          </option>
                        ))}
                        {hoyTodaviaSirve() && (
                          <option value="hoy930">Hoy 9:30 a.m.</option>
                        )}
                        <option value="manana930">Mañana 9:30 a.m.</option>
                        <option value="elegir">Elegir fecha y hora…</option>
                      </select>
                      {posponiendo === r.id && (
                        <span className="flex flex-wrap items-center gap-1">
                          <input
                            type="datetime-local"
                            className="input py-1 text-xs"
                            min={minDateTimeLocal()}
                            value={posponerFecha}
                            onChange={(e) => setPosponerFecha(e.target.value)}
                          />
                          <button
                            type="button"
                            className="text-xs text-accent-400 hover:text-accent-300 disabled:opacity-40"
                            disabled={!posponerFecha}
                            onClick={() => {
                              const cuando = new Date(posponerFecha);
                              if (Number.isNaN(cuando.getTime())) return;
                              setPosponiendo(null);
                              void accion(r, "posponer", { cuando });
                            }}
                          >
                            Posponer
                          </button>
                          <button
                            type="button"
                            className="text-xs text-zinc-500 hover:text-zinc-300"
                            onClick={() => setPosponiendo(null)}
                          >
                            Cancelar
                          </button>
                        </span>
                      )}

                      <button
                        onClick={() => void accion(r, "cancelar")}
                        className="text-xs text-zinc-500 hover:text-red-400"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
